import { SuperGridValidateFunction, SuperGridValidationEntity } from '@aleph/supergrid';
import { RedditLevel } from 'wb-reddit/app/routes/superGrid/utils';
import { validateRedditAd } from 'wb-reddit/app/routes/superGrid/validation/ad';
import { validateRedditCampaign } from 'wb-reddit/app/routes/superGrid/validation/campaign';
import { validateRedditGroup } from 'wb-reddit/app/routes/superGrid/validation/group';
import { string, ValidationError } from 'yup';
import { uniqBy } from 'lodash';
import { RedditCreateData, RedditCreateLevels } from 'wb-reddit/app/routes/superGrid/mutator/utils';

export const transformYupErrors = (errors: ValidationError, level: string): SuperGridValidationEntity[] => uniqBy(errors.inner?.map(
	error => ({
		id:      errors?.value?.id ?? errors?.value?.hash,
		message: error.message,
		// this is here because yup returns the path as "field[0]" for arrays or "field.nestedField" for nested validations
		field:   (error.path ?? '').split('.')?.[0]?.split('[')?.[0] ?? '',
		level,
	}),
) ?? [], (error) => `${error.id}_${error.field}_${error.level}`);

export const validateRedditEntity: SuperGridValidateFunction = async ({ changeEvent, level }) => {
	try {
		switch (level) {
			case RedditLevel.GROUP:
				await validateRedditGroup({
					...changeEvent.data,
					groupName: changeEvent.data.name,
				});

				return [];
			case RedditLevel.CAMPAIGN:
				await validateRedditCampaign({
					...changeEvent.data,
					campaignName: changeEvent.data.name,
				});

				return [];
			case RedditLevel.AD:
				await validateRedditAd({
					...changeEvent.data,
					adName: changeEvent.data.name,
				});

				return [];
		}
	} catch (e) {
		if (changeEvent?.node?.id) {
			return transformYupErrors(e as ValidationError, changeEvent?.node?.data?.supergridLevel);
		}
	}

	return [];
};

export const nameValidator = string()
	.required('Name is required')
	.test({
		name:    'name-too-short',
		message: 'Name must be at least 3 characters',
		test:    value => value.length >= 3,
	}).test({
		name:    'name-too-long',
		message: 'Name must not be longer than 200 characters',
		test:    value => value.length <= 200,
	});

export const preValidateRedditEntities = async (mutations: Record<RedditCreateLevels, RedditCreateData[]>) => {
	let preValidations: SuperGridValidationEntity[] = [];

	// eslint-disable-next-line no-restricted-syntax
	for (const campaign of mutations.campaign ?? []) {
		try {
			// eslint-disable-next-line no-await-in-loop
			await validateRedditCampaign({ ...campaign, campaignName: campaign.name });
		} catch (errors: unknown) {
			preValidations = [
				...preValidations,
				...transformYupErrors((errors as unknown as ValidationError) ?? [], campaign?.supergridLevel),
			];
		}
	}

	// eslint-disable-next-line no-restricted-syntax
	for (const group of mutations.group ?? []) {
		try {
			// eslint-disable-next-line no-await-in-loop
			await validateRedditGroup({ ...group, groupName: group.name });
		} catch (errors: unknown) {
			preValidations = [
				...preValidations,
				...transformYupErrors((errors as unknown as ValidationError) ?? [], group?.supergridLevel),
			];
		}
	}

	// eslint-disable-next-line no-restricted-syntax
	for (const ad of mutations.ad ?? []) {
		try {
			// eslint-disable-next-line no-await-in-loop
			await validateRedditAd({ ...ad, adName: ad.name });
		} catch (errors: unknown) {
			preValidations = [
				...preValidations,
				...transformYupErrors((errors as unknown as ValidationError) ?? [], ad?.supergridLevel),
			];
		}
	}

	return preValidations;
};
